(() => {
  if (window.grillAtlasInitialized) return;
  window.grillAtlasInitialized = true;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  // --------------------------------------------------------------------------
  // Intersection Observer for Smooth Scroll Revelations
  // --------------------------------------------------------------------------
  let observer;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('ga-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.body.classList.add('ga-js');
  }

  const observe = () => {
    document.querySelectorAll('.ga-reveal:not(.ga-visible)').forEach(el => observer?.observe(el));
  };
  observe();

  document.addEventListener('shopify:section:load', observe);
  document.addEventListener('shopify:section:select', event => {
    event.target.querySelectorAll('.ga-reveal').forEach(el => el.classList.add('ga-visible'));
  });

  // --------------------------------------------------------------------------
  // Header Scroll Elevation & Hero Parallax
  // --------------------------------------------------------------------------
  let frame = 0;
  const header = document.querySelector('.ga-header');

  const onScroll = () => {
    const scrollY = window.scrollY;

    // Header elevation
    if (header) {
      if (scrollY > 25) {
        header.classList.add('ga-scrolled');
      } else {
        header.classList.remove('ga-scrolled');
      }
    }

    // Hero parallax depth
    if (!reduced.matches && !frame) {
      frame = requestAnimationFrame(() => {
        const photo = document.querySelector('.ga-hero-photo');
        if (photo) {
          photo.style.setProperty('--ga-travel', `${Math.min(scrollY * 0.12, 90)}px`);
        }
        frame = 0;
      });
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      document.querySelector('.ga-hero-photo')?.style.removeProperty('--ga-travel');
    }
  });

  // --------------------------------------------------------------------------
  // Modal / Native Dialog Navigation & Finder
  // --------------------------------------------------------------------------
  let previousFocus;
  const openDialog = dialog => {
    if (!dialog) return;
    document.querySelectorAll('.ga-native-dialog[open]').forEach(el => el.close());
    previousFocus = document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('ga-dialog-open');
  };

  const closeDialog = dialog => {
    dialog?.close();
    document.documentElement.classList.remove('ga-dialog-open');
    previousFocus?.focus();
  };

  const resetFinder = () => {
    const form = document.getElementById('ga-finder-form');
    if (form) {
      form.reset();
      form.hidden = false;
    }
    const result = document.getElementById('ga-finder-result');
    if (result) result.hidden = true;
  };

  // --------------------------------------------------------------------------
  // Event Delegation for Clicks & Actions
  // --------------------------------------------------------------------------
  document.addEventListener('click', event => {
    const el = event.target.closest('button, a');
    if (!el) return;

    if (el.matches('[data-ga-finder]')) {
      event.preventDefault();
      resetFinder();
      openDialog(document.getElementById('ga-finder'));
    }

    if (el.matches('[data-ga-menu]')) {
      event.preventDefault();
      openDialog(document.getElementById('ga-mobile-nav'));
    }

    if (el.matches('[data-ga-close]')) {
      closeDialog(el.closest('dialog'));
    }

    if (el.matches('[data-ga-restart]')) {
      resetFinder();
      document.querySelector('#ga-finder-form input')?.focus();
    }

    if (el.matches('#ga-mobile-nav a')) {
      closeDialog(el.closest('dialog'));
    }

    if (el.matches('[data-ga-cart]')) {
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open();
      }
    }

    if (el.matches('[data-ga-search]')) {
      const search = document.getElementById('search-modal');
      if (search && typeof search.showDialog === 'function') {
        event.preventDefault();
        search.showDialog();
      }
    }

    if (el.matches('[data-ga-filter]')) {
      const section = el.closest('[data-ga-collection-section]');
      const filter = el.dataset.gaFilter;
      section.querySelectorAll('[data-ga-filter]').forEach(button => {
        const active = button === el;
        button.classList.toggle('ga-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      let visible = 0;
      section.querySelectorAll('[data-ga-fuel]').forEach(card => {
        card.hidden = filter !== 'all' && card.dataset.gaFuel !== filter;
        if (!card.hidden) visible++;
      });
      const empty = section.querySelector('[data-ga-empty]');
      if (empty) empty.hidden = visible !== 0;
    }
  });

  document.addEventListener('close', event => {
    if (event.target.matches('.ga-native-dialog')) {
      document.documentElement.classList.remove('ga-dialog-open');
      previousFocus?.focus();
    }
  }, true);

  document.addEventListener('click', event => {
    if (event.target.matches('.ga-native-dialog')) {
      const r = event.target.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) {
        closeDialog(event.target);
      }
    }
  });

  // --------------------------------------------------------------------------
  // Grill Finder Quiz Handling
  // --------------------------------------------------------------------------
  document.addEventListener('submit', event => {
    if (event.target.id !== 'ga-finder-form') return;
    event.preventDefault();
    const data = new FormData(event.target);
    const fuel = data.get('fuel');
    const finder = document.getElementById('ga-finder');
    const labels = {
      gas: 'Gas is your kind of fire.',
      charcoal: 'Take the charcoal trail.',
      pizza: 'Pizza night is calling.'
    };
    finder.querySelector('[data-ga-result-heading]').textContent = labels[fuel] || labels.gas;
    finder.querySelector('[data-ga-result-copy]').textContent =
      data.get('people') === 'crowd'
        ? 'Look for generous cooking space and a separate prep area to keep hosting relaxed.'
        : 'Start with a compact setup that gives you room to cook without taking over your space.';
    finder.querySelector('[data-ga-result-link]').href = finder.getAttribute(`data-${fuel}-url`);
    event.target.hidden = true;
    const result = document.getElementById('ga-finder-result');
    result.hidden = false;
    result.focus();
  });

  // --------------------------------------------------------------------------
  // Cart Badge Bump on Update
  // --------------------------------------------------------------------------
  document.addEventListener('shopify:cart:lines-update', event => {
    event.promise?.then(({ cart, detail }) => {
      const count = cart?.totalQuantity ?? detail?.itemCount;
      if (typeof count === 'number') {
        document.querySelectorAll('[data-ga-cart-count]').forEach(el => {
          el.textContent = String(count);
          el.classList.remove('ga-badge-updated');
          void el.offsetWidth; // Trigger reflow for animation restart
          el.classList.add('ga-badge-updated');
        });
      }
    }).catch(() => {});
  });

  // --------------------------------------------------------------------------
  // Product Reviews Smooth Scroll
  // --------------------------------------------------------------------------
  document.addEventListener('click', event => {
    const trigger = event.target.closest('.ga-rating-count');
    if (trigger) {
      const target = document.querySelector('.ga-reviews') || document.querySelector('[id*="atlas_reviews"]');
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });
})();
